import { useQuery } from "@tanstack/react-query";
import { apiClient, apiErrorMessage } from "../api/client";
import type { Campaign, UserAccount } from "../api/types";

/* What the campaign screens share: where a campaign lives, how its row is
 * read, and the words for its stages, its files and its gaps.
 *
 * Every decision about a campaign (its stage, where it stands, the next step,
 * its figures) is the engine's, in api.campaign (engine migration 200). These
 * are words and addresses. docs/plans/campaigns-overhaul.md. */

/** A campaign's page. The slug is its address and prints nowhere. */
export const campaignPath = (name: string, tab?: string) =>
  `/campaigns/${encodeURIComponent(name)}${tab ? `?tab=${tab}` : ""}`;

/** One campaign's row from api.campaign, by its slug. */
export function useCampaign(token: string, name: string) {
  return useQuery({
    queryKey: ["campaign", name],
    // While something runs for it, the page follows along. Nothing reorders.
    refetchInterval: (q) => {
      const c = q.state.data as Campaign | null | undefined;
      if (!c) return false;
      const busy = c.list_job_status === "queued" || c.list_job_status === "running"
        || c.export_job_status === "queued" || c.export_job_status === "running"
        || c.enrich_job_status === "queued" || c.enrich_job_status === "running"
        || (c.web_waiting ?? 0) > 0 || (c.apollo_open ?? 0) > 0;
      return busy ? 15_000 : false;
    },
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("campaign").select("*").eq("name", name).limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? [])[0] ?? null) as Campaign | null;
    },
  });
}

/** The people who can sign in and own a campaign, by name (api.user_account,
 *  engine migration 213), for the owner pickers and the Owner filter. */
export function useUserAccounts(token: string) {
  return useQuery({
    queryKey: ["user-accounts"],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("user_account").select("id,label,email").order("label").limit(500);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as UserAccount[];
    },
  });
}

/* The stored value draft is shown as Planning (James, 2026-10-19, engine
   migration 256, which words it the same way in lake.campaign_stage_word). */
/** How many days before the calling date the builder and the dates dialog
 *  suggest starting enrichment: the engine's setting (api.schedule_setting,
 *  engine migration 256), so the number is not typed into the screen. */
export function useScheduleSetting(token: string) {
  return useQuery({
    queryKey: ["schedule-setting"],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("schedule_setting").select("lead_days").limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      const days = ((data ?? [])[0] as { lead_days?: number | null } | undefined)?.lead_days;
      if (typeof days !== "number") throw new Error("The engine did not say how many days before calling to start enrichment.");
      return days;
    },
  });
}

export const STAGE_WORDS: Record<string, string> = {
  draft: "Planning",
  enriching: "Enriching",
  ready_to_call: "Ready to call",
  calling: "Calling",
  completed: "Completed",
  abandoned: "Abandoned",
};

export const stageWord = (s: string | null | undefined) => (s && STAGE_WORDS[s]) || s || "";

/** The badge tone for a stage. */
export function stageTone(s: string | null | undefined): "ok" | "waiting" | "quiet" {
  if (s === "ready_to_call" || s === "calling") return "ok";
  if (s === "draft" || s === "enriching") return "waiting";
  return "quiet";
}

/* The three files a campaign exports, by job kind and by the snapshot
   destination the engine writes for each. */
export const FILES = {
  orum: { kind: "orum_export", fn: "build_orum_export", word: "Orum list", says: "One row per person with a phone, call notes on each row. For Orum." },
  lead_sheet: { kind: "lead_sheet", fn: "build_lead_sheet", word: "Lead Sheet", says: "The workbook the team reviews, one block per property." },
  crm: { kind: "crm_export", fn: "build_crm_export", word: "CRM CSV", says: "Companies and contacts, for HubSpot." },
} as const;
export type FileKey = keyof typeof FILES;

const BY_KIND: Record<string, string> = {
  orum_export: "Orum list", lead_sheet: "Lead Sheet", crm_export: "CRM CSV",
  orum: "Orum list", csv_export: "CRM CSV",
};
/** The file's word, from a job kind or a snapshot destination. */
export const fileWord = (k: string | null | undefined) => (k && BY_KIND[k]) || k || "";

/** A person's name from their address, the rule the engine's line uses. */
export function nameOf(email: string | null | undefined): string {
  if (!email) return "";
  // The engine's workers write their own name (campaign_measure), not an
  // address. That is EVIE doing it.
  if (!email.includes("@")) return "EVIE";
  const local = email.split("@")[0] ?? "";
  return local ? local.charAt(0).toUpperCase() + local.slice(1) : email;
}

/* The gaps a measurement counts (ingestion/enrich_policy.py), as the areas
   for further enrichment on a campaign's Enrichment tab, grouped by kind:
   people, properties and property owners. The group's heading names the
   kind, so the rows do not repeat it. "Property owners", because a
   campaign has an owner too, the person who runs it (engine migration 214). */
const GAP_KINDS: { kind: string; label: string }[] = [
  { kind: "people", label: "People" },
  { kind: "properties", label: "Properties" },
  { kind: "companies", label: "Property owners" },
];

/** The engine's words for each gap, by key: "No mobile" for no_mobile
 *  (api.gap_word, engine migration 251). Each says what the records have, not
 *  what they need, because the table names where more data would help
 *  rather than faults to clear. The records section reads the same words. */
export function useGapWords(token: string) {
  return useQuery({
    queryKey: ["gap-words"],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("gap_word").select("key,label").limit(100);
      if (error) throw new Error(apiErrorMessage(error));
      const map = new Map<string, string>();
      for (const r of (data ?? []) as { key?: string | null; label?: string | null }[]) {
        if (r.key && r.label) map.set(r.key, r.label);
      }
      return map;
    },
  });
}

export type GapRow = { key: string; words: string; n: number };
export type GapGroup = { kind: string; label: string; rows: GapRow[] };

/** A measurement's gaps by kind, in the order people, properties, property owners,
 *  each kind's reasons biggest first, in the engine's words. A kind with no
 *  gaps is left out. A key the engine has no words for shows as the key. */
export function gapGroups(gaps: unknown, words: Map<string, string>): GapGroup[] {
  if (!gaps || typeof gaps !== "object") return [];
  const all = gaps as Record<string, unknown>;
  const out: GapGroup[] = [];
  for (const g of GAP_KINDS) {
    const counts = all[g.kind];
    if (!counts || typeof counts !== "object") continue;
    const rows: GapRow[] = [];
    for (const [gap, n] of Object.entries(counts as Record<string, unknown>)) {
      if (typeof n !== "number" || n <= 0) continue;
      rows.push({ key: `${g.kind}.${gap}`, words: words.get(gap) ?? gap.replace(/_/g, " "), n });
    }
    if (rows.length) out.push({ kind: g.kind, label: g.label, rows: rows.sort((a, b) => b.n - a.n) });
  }
  return out;
}

/** Said wherever a large web run is confirmed, which the engine decides
 *  (api.web_sweep_size: more records than enrichment.web_confirm_records,
 *  500). Each record means web searches, and AI reads the pages they find.
 *  James, 2026-10-23, in place of a sentence about the daily limit on model
 *  calls and the hours the queue would take. */
export const WEB_COST = "There are a lot of records here, so this will cost money to process. Other campaigns' web enrichment waits until these are done.";
